/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Box, ListItem } from '@mui/material';
import Terminal, { NetworkGroup } from '../../shared/entities/terminal';
import { convertTerminalListIndexToTerminalTabIndex } from '../../utils/TerminalListDrawerUtils';
import { getConnectivityIcon } from '../../utils/ConnectionStatusUtils';
import appTheme from '../common/theme/appTheme.json';
import PlatformListListItemDataDefault from './PlatformListListItemDataDefault';
import PlatformListListItemDataLinkway from './PlatformListListItemDataLinkway';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    collapseListItem: {
      backgroundColor: '#141D24',
    },
    leftMultiline: {
      marginTop: '0px',
      marginBottom: '0px',
    },
    leftPrimaryActive: {
      color: '#5EA215',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '24px',
    },
    leftPrimaryStandby: {
      color: '#C5F042',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '24px',
    },
    leftPrimaryOffline: {
      color: '#C3CDD5',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '24px',
    },
    leftSecondary: {
      color: '#ffffff',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '16px',
    },
    rightPrimary: {
      color: '#202E39',
      backgroundColor: '#61DED8',
      fontSize: '12px',
      letterSpacing: 'normal',
      padding: '0px 4px',
      boxSizing: 'border-box',
      fontWeight: 'bold',
      display: 'inline',
      lineHeight: '24px',
    },
    rightPrimaryHidden: {
      color: 'rgba(0,0,0,0)',
      backgroundColor: 'rgba(0,0,0,0)',
      fontSize: '12px',
      letterSpacing: 'normal',
      padding: '0px 4px',
      boxSizing: 'border-box',
      fontWeight: 'bold',
      lineHeight: '24px',
    },
    rightSecondary: {
      color: '#ffffff',
      fontSize: '14px',
      letterSpacing: '0.4px',
      lineHeight: '16px',
    },
    rightMultiline: {
      textAlign: 'right',
      borderRadius: '1px',
      marginTop: '0px',
      marginBottom: '0px',
    },
  }),
);

export default function CollapseListItem(props: {
  terminal: Terminal;
  primaryDatapathUuidInGroup: string;
  index: number;
  total: number;
  selected: boolean;
  hideTtho: boolean;
  onRowClickedHandler: (datapathUuid: string, tabIndex: number | undefined) => void;
}) {
  const { terminal, primaryDatapathUuidInGroup, index, total, selected, hideTtho, onRowClickedHandler } = props;
  const classes = useStyles();

  const connectivityIcon = useMemo(
    () => (
      <Box data-test="terminal-list-item-text__connectivity-icon" width="14px" marginLeft="42px" marginRight="26px">
        {getConnectivityIcon(terminal.connectivityStatus)}
      </Box>
    ),
    [terminal.connectivityStatus],
  );
  return (
    <ListItem
      data-test={`platform-list-item__terminals-on-aircraft-list-item__${terminal.datapathUuid}`}
      className={classes.collapseListItem}
      selected={selected}
      sx={{
        paddingTop: !index ? '13px !important' : '5px !important',
        paddingBottom: index === total - 1 ? '17px !important' : '5px !important',
        backgroundColor: selected ? '#000000 !important' : appTheme.primaryBackgroundColor,
      }}
      button
      onClick={(event: React.MouseEvent) => {
        onRowClickedHandler(
          primaryDatapathUuidInGroup,
          convertTerminalListIndexToTerminalTabIndex(index + 1, terminal.installationType),
        );
      }}
    >
      {connectivityIcon}
      {terminal.networkGroup === NetworkGroup.LINKWAY ? (
        <PlatformListListItemDataLinkway terminal={terminal} classes={classes} />
      ) : (
        <PlatformListListItemDataDefault terminal={terminal} classes={classes} hideTtho={hideTtho} />
      )}
    </ListItem>
  );
}
